import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BookingCalendar from './BookingCalendar';
import { logoSrc } from '../utils/logoSrc';
import './ProviderPageNext.css';

// The provider's public page as a visitor sees it — the cleanup.
// Ivan's month calendar (BookingCalendar.js, his own design) is the one
// instrument on the page and is mounted exactly as it is; everything
// around it is the cleanup he asked for (2026-09-27): a letterhead
// instead of the name and title twice, no instruction sentences, no
// welcome modal, no eyebrow-and-paragraph sections, the price ledger and
// the venues once, one Book button. Orientation only: the page captures
// nothing; Book opens the wizard at /p/:joinCode/book.
//
// Mounted at /p/bookingpagenew with Ivan's join code for review; on his
// go it replaces PublicProviderProfile's visitor branch. Reads only the
// public endpoints the incumbent reads.

const firstSentence = (text) => {
  const t = String(text || '').trim();
  const m = t.match(/^(.+?[.!?])(\s+|$)/);
  return m ? { first: m[1], rest: t.slice(m[0].length).trim() } : { first: t, rest: '' };
};

export default function ProviderPageNext({ joinCode }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [state, setState] = useState('loading');           // loading | ready | missing
  const [catalogs, setCatalogs] = useState({});             // 'mobile' | venueId → basePricing rows
  const [selectedDay, setSelectedDay] = useState(null);
  const [bioOpen, setBioOpen] = useState(false);

  // Identity, venues, contact, the standard menu.
  useEffect(() => {
    let cancelled = false;
    fetch(`/api/join-code/profile/${encodeURIComponent(joinCode)}`)
      .then(r => (r.ok ? r.json() : null))
      .then(json => {
        if (cancelled) return;
        if (!json) { setState('missing'); return; }
        setProfile(json);
        setState('ready');
      })
      .catch(() => { if (!cancelled) setState('missing'); });
    return () => { cancelled = true; };
  }, [joinCode]);

  // Prices per place: the public menu, plus each venue's own menu.
  const venueIdsKey = (profile?.venues || []).map(v => v.id).join(',');
  useEffect(() => {
    if (!profile?.joinCode) return undefined;
    let cancelled = false;
    const load = (key, query) => fetch(`/api/public/${profile.joinCode}/services${query}`)
      .then(r => (r.ok ? r.json() : null))
      .then(json => { if (!cancelled && json) setCatalogs(prev => ({ ...prev, [key]: json.basePricing || [] })); })
      .catch(() => { /* that place contributes no column */ });
    load('mobile', '');
    (venueIdsKey ? venueIdsKey.split(',') : []).forEach(id => load(id, `?locationId=${id}`));
    return () => { cancelled = true; };
  }, [profile?.joinCode, venueIdsKey]);

  const places = useMemo(() => {
    const venues = profile?.venues || [];
    return [{ id: 'mobile', name: 'In-home' }, ...venues.map(v => ({ id: v.id, name: v.name, address: v.address }))];
  }, [profile?.venues]);

  // Ledger: one row per length, one column per place that sells it.
  const ledger = useMemo(() => {
    const lengths = new Set();
    for (const p of places) for (const row of (catalogs[p.id] || [])) if (Number(row.price) > 0) lengths.add(Number(row.duration));
    return [...lengths].sort((a, b) => a - b).map(minutes => ({
      minutes,
      prices: places.map(p => {
        const hit = (catalogs[p.id] || []).find(r => Number(r.duration) === minutes && Number(r.price) > 0);
        return hit ? Number(hit.price) : null;
      }),
    }));
  }, [catalogs, places]);
  const hasVenues = places.length > 1;

  const firstName = (profile?.providerName || '').split(' ')[0] || '';
  const businessName = profile?.businessName || profile?.providerName || 'Provider';
  const bio = firstSentence(profile?.bio);
  const bookable = (profile?.publicBookingMode || 'on') === 'on';
  const book = () => navigate(`/p/${profile.joinCode}/book`);
  const contacts = profile ? [
    profile.contact?.text !== false && profile.providerPhone && { key: 'text', label: 'Text', href: `sms:${String(profile.providerPhone).replace(/[^+\d]/g, '')}` },
    profile.contact?.call !== false && profile.providerPhone && { key: 'call', label: 'Call', href: `tel:${String(profile.providerPhone).replace(/[^+\d]/g, '')}` },
    profile.contact?.email !== false && profile.contactEmail && { key: 'email', label: 'Email', href: `mailto:${profile.contactEmail}` },
  ].filter(Boolean) : [];

  if (state === 'loading') return <div className="pn pn-state" aria-busy="true"><span className="pn-spinner" aria-hidden="true" /></div>;
  if (state === 'missing' || !profile) {
    return (
      <div className="pn pn-state">
        <p className="pn-missing">That page isn't here. Check the link you were sent.</p>
      </div>
    );
  }

  return (
    <div className="pn av-paper" data-theme="boutique">
      <header className="pn-band">
        <div className="pn-band-in">
          <span className="pn-logo" aria-hidden={!profile.logoUrl}>
            {profile.logoUrl
              ? <img src={logoSrc(profile.logoUrl, 44)} alt="" width="44" height="44" />
              : <span className="pn-logo-initial">{businessName.slice(0, 1)}</span>}
          </span>
          <h1 className="pn-name">{businessName}</h1>
          <nav className="pn-band-r" aria-label="Account">
            <Link to="/login" className="pn-signin">Sign in</Link>
            {bookable && <button type="button" className="pn-book pn-book-band" onClick={book}>Book a session</button>}
          </nav>
        </div>
      </header>

      <main className="pn-body">
        <div className="pn-main">
          {profile.outOfOffice && (
            <p className="pn-away" role="note">
              <strong>{profile.outOfOffice.active ? 'Out of office.' : 'Time away.'}</strong> {profile.outOfOffice.message}
            </p>
          )}

          <section className="pn-letterhead" aria-label={`About ${firstName || businessName}`}>
            {profile.providerPhotoUrl && (
              <img className="pn-photo" src={profile.providerPhotoUrl} alt={profile.providerName || businessName} width="72" height="72" />
            )}
            <div className="pn-intro">
              {bio.first ? <p className="pn-bio">{bio.first}</p> : <p className="pn-bio">{businessName}</p>}
              {bio.rest && (
                <>
                  <p className="pn-bio-rest" hidden={!bioOpen}>{bio.rest}</p>
                  <button type="button" className="pn-more" aria-expanded={bioOpen} onClick={() => setBioOpen(o => !o)}>
                    {bioOpen ? 'Less' : `More about ${firstName || 'them'}`}
                  </button>
                </>
              )}
            </div>
          </section>

          {/* Ivan's calendar, exactly as it is. On phones it sits here,
              right under the letterhead; at ≥1024px the grid moves it
              into the right column beside the ledger. */}
          <section className="pn-calendar" aria-label={`When ${firstName || businessName} is free`}>
            <BookingCalendar
              joinCode={profile.joinCode}
              providerFirstName={firstName}
              isSignedIn={false}
              savedAddress={null}
              venue={null}
              venueName={null}
              onOpenDay={(day) => setSelectedDay(day.date)}
              selectedDate={selectedDay}
              showCaption={false}
            />
          </section>

          <section className="pn-ledger" aria-label="Prices">
            <h2 className="pn-h2">Massage</h2>
            <table>
              {hasVenues && (
                <thead>
                  <tr>
                    <th scope="col"><span className="pn-sr">Length</span></th>
                    {places.map(p => <th key={p.id} scope="col">{p.id === 'mobile' ? 'In-home' : p.name}</th>)}
                  </tr>
                </thead>
              )}
              <tbody>
                {ledger.map(r => (
                  <tr key={r.minutes}>
                    <th scope="row">{r.minutes} min</th>
                    {r.prices.map((p, i) => <td key={i}>{p == null ? <span className="pn-dash">—</span> : `$${p}`}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            {hasVenues && (
              <dl className="pn-places">
                {places.map(p => (
                  <div key={p.id}>
                    <dt>{p.id === 'mobile' ? 'In-home' : p.name}</dt>
                    <dd>{p.id === 'mobile' ? `${firstName || 'The therapist'} comes to your address.` : p.address}</dd>
                  </div>
                ))}
              </dl>
            )}
          </section>

          {contacts.length > 0 && (
            <section className="pn-contact" aria-label="Contact">
              <span className="pn-contact-lead">A question first?</span>
              {contacts.map(c => <a key={c.key} href={c.href} className="pn-contact-link">{c.label}</a>)}
            </section>
          )}
        </div>
      </main>

      <footer className="pn-foot">
        <span className="pn-powered">Powered by</span>
        <img src="/imgs/avayble_logo.png" alt="Avayble" width="250" height="60" />
      </footer>

      {bookable && (
        <div className="pn-sticky">
          <button type="button" className="pn-book pn-book-sticky" onClick={book}>Book a session</button>
        </div>
      )}
    </div>
  );
}
